import { request } from "../../../services/api"
import type {
  BaseApiResponse,
  BaseFilters,
  BasePagination
} from "../../../types/api.ts"
import { createFormDataNested } from "../../../utils/form-data/create-form-data-nested"
import type { Lead } from "../../Lead/types/lead.types.ts"
import type {
  ProjectData,
  ProjectFilters,
  ProjectsState
} from "../redux/projects.types.ts"
import type { ImportProjectsData } from "./services.types.ts"

const BASE_URL = "/project-management"
const PROJECTS_BASE_URL = "/project-management/projects"

const PATHS = {
  getProjects: `${PROJECTS_BASE_URL}/`,
  getFilterOptions: `${PROJECTS_BASE_URL}/filter_options/`,
  deleteProject: (id: string) => `${PROJECTS_BASE_URL}/${id}/`,
  archiveProject: (id: string) => `${PROJECTS_BASE_URL}/${id}/archive/`,
  restoreProject: (id: string) => `${PROJECTS_BASE_URL}/${id}/restore/`,

  // Import
  importProjects: `${PROJECTS_BASE_URL}/import-data/`,
  importTemplate: `${PROJECTS_BASE_URL}/import-template/`,

  // //
  getLeadApproval: `${BASE_URL}/lead-approval/`,
  approveLead: (id: string) => `${BASE_URL}/lead-approval/${id}/approve/` // convert a lead into a project
}
export const getProjects = async (params?: Partial<ProjectFilters>) => {
  return request.api.get<ProjectData, BaseApiResponse<unknown>>(
    PATHS.getProjects,
    params
  )
}

export const deleteProject = (id: string) => {
  return request.api.delete<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.deleteProject(id)
  )
}

export const archiveProject = ({
  id,
  cancel_appointments
}: {
  id: string
  cancel_appointments?: boolean
}) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.archiveProject(id),
    cancel_appointments ? { cancel_appointments: true } : {}
  )
}

export const restoreProject = (id: string) => {
  return request.api.post<BaseApiResponse<null>, BaseApiResponse<unknown>>(
    PATHS.restoreProject(id)
  )
}

// The list edits a project through the detail app's PATCH endpoint — the same
// call `updateListProject` / `updateProjectStatus` make. Re-exported so the
// list's own bulk thunks read from one services module.
export { patchProject } from "../../Project/api/project.services.ts"

export const importProjects = (file: File) => {
  const formData = createFormDataNested({ file }, "file")

  return request.api.post<ImportProjectsData, Blob>(
    PATHS.importProjects,
    formData,
    {
      responseType: "blob",
      headers: { "Content-Type": "multipart/form-data" }
    }
  )
}

export const getImportProjectsTemplates = () => {
  return request.api.get<Blob, BaseApiResponse<unknown>>(
    PATHS.importTemplate,
    {},
    { responseType: "blob" }
  )
}

export const getProjectsFilterOptions = async () => {
  return request.api.get<
    ProjectsState["filterOptions"],
    BaseApiResponse<unknown>
  >(PATHS.getFilterOptions)
}

export const getLeadsApproval = async (
  params: Pick<BaseFilters, "page" | "page_size">
) => {
  return request.api.get<BasePagination<Lead>, BaseApiResponse<unknown>>(
    PATHS.getLeadApproval,
    params
  )
}

export const approveLead = async (
  id: string,
  project_manager_id: string
) => {
  return request.api.post<BaseApiResponse<unknown>, BaseApiResponse<unknown>>(
    PATHS.approveLead(id),
    { project_manager_id }
  )
}
